import { Fragment, useState, useEffect } from 'react'
import cx from 'classnames'
import { RenderItemParams } from 'tree-dnd'

import { LocationPickerFolder, SelectedFolder } from 'components/LocationPicker/LocationPicker'
import { TreeItemIcon } from 'components/FilesTree/components/TreeItemIcon/TreeItemIcon'
import { HandbookProvider } from 'components/Handbook2_0/Sidebar/HandbookProvider'
import { HandbookItems } from '../../HandbookItems/HandbookItems'
import { NavItem } from 'components/NavItem'

import HandbookItemsStyles from '../../HandbookItems/HandbookItems.module.scss'
import { ExpandButton } from 'components/NavItem/ExpandButton/ExpandButton'
import { Check } from 'blaze-ui/icons/16'

interface TreeRowProps extends RenderItemParams {
  onSelect: (folder: SelectedFolder | null) => void
  activeFolder?: LocationPickerFolder | null | undefined
  disabledFolderIds?: string[]
  hideDocs?: boolean
}

export const TreeRow = ({ provided, item, onSelect, activeFolder, hideDocs, disabledFolderIds = [] }: TreeRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const isDatabase = item.data.database_mode
  const isFolder = item.data.type === 'folder'
  const isDisabled = disabledFolderIds.includes(item.data.id) || !item.data.has_edit_access
  const isCurrentFolder = !!activeFolder?.id && isFolder && activeFolder.id === item.id
  const showExpand = !isDatabase || (hideDocs && item.data.has_subprojects)

  useEffect(() => {
    if (!activeFolder || !isFolder) return

    const isItemActiveParent = activeFolder.ancestor_keys.includes(item.id as string)
    const isItemActive = activeFolder.id === item.data.id

    // expand ancestors of target folder, so target folder is visible on tree
    if ((isItemActiveParent || isItemActive) && !item.isExpanded && (!hideDocs || item.data.has_subprojects)) {
      setIsExpanded(true)
    }

    // doesn't fire hook when closing folder since we've excluded item.isExpanded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolder])

  return (
    <Fragment key={item.data.key}>
      <div ref={provided.innerRef} className={cx({ [HandbookItemsStyles.disabled]: isDisabled })}>
        <NavItem.Root
          isActive={isCurrentFolder}
          elementType="div"
          noMargin
          onPress={() => {
            if (isDisabled) return

            onSelect(item.data)
          }}
        >
          <NavItem.Emoji>
            <TreeItemIcon treeItem={item.data} isExpanded={isExpanded} />
          </NavItem.Emoji>
          <NavItem.Label label={item.data.title} />
          {showExpand && <ExpandButton isExpanded={isExpanded} onPress={() => setIsExpanded(!isExpanded)} />}
          {isCurrentFolder && (
            <div className={HandbookItemsStyles.checkIcon}>
              <Check />
            </div>
          )}
        </NavItem.Root>
      </div>
      {isExpanded && item.data.handbook?.key && (
        <HandbookProvider handbookKey={item.data.handbook.key}>
          <HandbookItems depthOffset={1} onSelect={onSelect} activeFolder={activeFolder} />
        </HandbookProvider>
      )}
    </Fragment>
  )
}
